import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { Button, Col, Form, FormFeedback, Input, Label, Row } from "reactstrap"
import Select from "react-select"
import { Countries } from "./countries"
import { onAddnewUser, onupdateUser, getUserDevices, setPrimaryDevice } from "store/userlist/action"
import PrimaryDeviceModal from "./PrimaryDeviceModal"

const NONE_DEVICE_OPTION = { value: "none", label: "None", trackingDeviceId: null, deviceUuid: null }

const UserSettingForm = ({
  selectedRow,
  isEdit,
  handleCancel,
  toggle,
  companyid,
  accessgrouplist,
}) => {
  const dispatch = useDispatch()
  const [primaryDevice, setPrimaryDeviceValue] = useState(NONE_DEVICE_OPTION)
  const [originalPrimaryDeviceId, setOriginalPrimaryDeviceId] = useState(null)
  const [showPrimaryModal, setShowPrimaryModal] = useState(false)

  const { userDevices } = useSelector(state => ({
    userDevices: state?.userlist?.userDevices,
  }))

  const deviceOptions = (userDevices?.data || []).map(device => ({
    value: device.trackingDeviceId,
    label: `${device.deviceName} (${device.ipAddress})`,
    trackingDeviceId: device.trackingDeviceId,
    deviceUuid: device.deviceUuid,
  }))

  // Fetch devices when the edit form opens for a user
  useEffect(() => {
    if (isEdit && selectedRow?.id) {
      dispatch(getUserDevices(selectedRow.id))
    }
  }, [isEdit, selectedRow?.id])

  // Preselect the current primary device once devices are loaded
  useEffect(() => {
    if (isEdit && userDevices?.data) {
      const current = userDevices.data.find(device => device.isPrimary)
      if (current) {
        const selected = {
          value: current.trackingDeviceId,
          label: `${current.deviceName} (${current.ipAddress})`,
          trackingDeviceId: current.trackingDeviceId,
          deviceUuid: current.deviceUuid,
        }
        setPrimaryDeviceValue(selected)
        setOriginalPrimaryDeviceId(current.trackingDeviceId)
      } else {
        setPrimaryDeviceValue(NONE_DEVICE_OPTION)
        setOriginalPrimaryDeviceId(null)
      }
    }
  }, [isEdit, userDevices])

  // const re = /^[0-9\b]+$/
  const phoneRegExp =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/
  // Validation

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      id: selectedRow.id,
      user_id: isEdit ? selectedRow && selectedRow?.userIdentificationNumber : "",
      email: isEdit ? selectedRow && selectedRow?.useremail : "",
      firstname: isEdit ? selectedRow && selectedRow?.firstname : "",
      lastname: isEdit ? selectedRow && selectedRow?.lastname : "",
      contactnumber: isEdit ? selectedRow && selectedRow?.contactnumber : "",
      // country: isEdit ? selectedRow && selectedRow?.country : "India",
      contactname: isEdit ? selectedRow && selectedRow?.username : "",
      phonenumber: isEdit ? selectedRow && selectedRow?.phonenumber : "",
      active: isEdit ? (selectedRow && selectedRow.active !== null ? selectedRow.active : true) : true,
      viewonly: isEdit ? (selectedRow && selectedRow.viewonly !== null ? selectedRow.viewonly : false) : false,
      role: isEdit ? selectedRow && selectedRow?.accessgroupid : "",
    },
    validationSchema: Yup.object().shape({
      user_id: Yup.string().required("User Id is Required"),
      email: Yup.string().email("Invalid email").required("Email is Required"),
      firstname: Yup.string().required("First Name is Required"),
      lastname: Yup.string().required("Last Name is Required"),
      role: Yup.string().required("Please Select Role"),
      contactnumber: Yup.string()
        .matches(phoneRegExp, "Phone number is not valid")
        .min(10, "Contact Number must be at least 10 characters")
        .max(12, "Contact Number must be at most 12 characters")
        .required("Contact Number is Required"),
    }),
    onSubmit: values => {
      if (isEdit) {
        const updateUser = {
          userId: values?.id,
          companyid: companyid,
          email: values?.email,
          firstname: values?.firstname,
          lastname: values?.lastname,
          // country: values?.country,
          active: values?.active,
          viewonly: values?.viewonly,
          contactname: values?.contactname,
          contactnumber: values?.contactnumber,
          accessgroupid: parseInt(values?.role),
          userIdentificationNumber: values.user_id
        }
        // update customer
        dispatch(onupdateUser(updateUser, handleResponse))

        // set primary computer if the selection changed
        const selectedDeviceId = primaryDevice?.trackingDeviceId ?? null
        if (selectedDeviceId !== originalPrimaryDeviceId) {
          dispatch(
            setPrimaryDevice({
              userid: values?.id,
              trackingDeviceId: selectedDeviceId,
              deviceUuid: selectedDeviceId ? primaryDevice?.deviceUuid : null,
            })
          )
        }
      } else {
        const newUser = {
          companyid: companyid,
          email: values?.email,
          firstname: values?.firstname,
          lastname: values?.lastname,
          // country: values?.country,
          active: values?.active,
          viewonly: values?.viewonly,
          contactname: values?.contactname,
          contactnumber: values?.contactnumber,
          accessgroupid: parseInt(values?.role),
          userIdentificationNumber: values.user_id
        }
        // save new customer
        dispatch(onAddnewUser(newUser, handleResponse))
      }
    },
  })
  const handleResponse = status => {
    if (status === "SUCCESS") {
      toggle()
      validation.resetForm()
    }
  }
  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        validation.handleSubmit()
        return false
      }}
    >
      <Row form>
        <Col className="col-12">
          <Row>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">User Id</Label>
                <Input
                  id="user_id"
                  name="user_id"
                  className="form-control"
                  placeholder="Enter User Id"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values?.user_id || ""}
                  invalid={
                    validation.touched.user_id && validation.errors.user_id
                      ? true
                      : false
                  }
                />
                {validation.touched.user_id && validation.errors.user_id ? (
                  <FormFeedback type="invalid">
                    {validation.errors.user_id}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">Contact Number</Label>
                <Input
                  id="contactnumber"
                  name="contactnumber"
                  className="form-control"
                  placeholder="Enter Contact Number"
                  type=""
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values?.contactnumber || ""}
                  invalid={
                    validation.touched.contactnumber &&
                    validation.errors.contactnumber
                      ? true
                      : false
                  }
                />
                {validation.touched.contactnumber &&
                validation.errors.contactnumber ? (
                  <FormFeedback type="invalid">
                    {validation.errors.contactnumber}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">Email</Label>
                <Input
                  id="email"
                  name="email"
                  className="form-control"
                  placeholder="Enter Email"
                  type="email"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values?.email || ""}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">Contact Name</Label>
                <Input
                  id="contactname"
                  name="contactname"
                  className="form-control"
                  placeholder="Enter Contact Name"
                  type="text"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values?.contactname || ""}
                  invalid={
                    validation.touched.contactname &&
                    validation.errors.contactname
                      ? true
                      : false
                  }
                />
                {validation.touched.contactname &&
                validation.errors.contactname ? (
                  <FormFeedback type="invalid">
                    {validation.errors.contactname}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">First Name</Label>
                <Input
                  name="firstname"
                  type="text"
                  placeholder="Enter First name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values?.firstname || ""}
                  invalid={
                    validation.touched.firstname && validation.errors.firstname
                      ? true
                      : false
                  }
                />
                {validation.touched.firstname && validation.errors.firstname ? (
                  <FormFeedback type="invalid">
                    {validation.errors.firstname}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">Role</Label>
                <Input
                  id="role"
                  type="select"
                  name="role"
                  value={validation.values?.role || ""}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  invalid={
                    validation.touched.role && validation.errors.role
                      ? true
                      : false
                  }
                >
                  <option key={`optionVal_empty`} value="">
                    Please Select Roles
                  </option>
                  {(accessgrouplist?.data || [])?.map((option, i) => (
                    <option key={`optionVal_${i}`} value={option?.id}>
                      {option?.accessGroupName}
                    </option>
                  ))}
                  <option key={`User`} value="6"  label = "User">
                    User
                  </option>     
                </Input>

                {validation.touched.role && validation.errors.role ? (
                  <FormFeedback type="invalid">
                    {validation.errors.role}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
          {isEdit ? (
            <Row>
              <Col sm={6}>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Label className="form-label mb-0">Primary Computer</Label>
                    <Button
                      type="button"
                      color="link"
                      className="p-0"
                      onClick={() => setShowPrimaryModal(true)}
                    >
                      Manage history
                    </Button>
                  </div>
                  {deviceOptions.length > 0 ? (
                    <Select
                      id="primaryDevice"
                      name="primaryDevice"
                      classNamePrefix="select2-selection"
                      value={primaryDevice}
                      onChange={value => setPrimaryDeviceValue(value || NONE_DEVICE_OPTION)}
                      options={[NONE_DEVICE_OPTION, ...deviceOptions]}
                    />
                  ) : (
                    <div className="text-muted">No tracked computers yet</div>
                  )}
                </div>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col sm={6}>
              <div className="mb-3">
                <Label className="form-label">Last Name</Label>
                <Input
                  name="lastname"
                  type="text"
                  placeholder="Enter Last name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  // value={validation.values?.lastname.replace(/[^A-Za-z]/ig, '') || ""}
                  value={validation.values?.lastname || ""}
                  
                  invalid={
                    validation.touched.lastname && validation.errors.lastname
                      ? true
                      : false
                  }
                />
                {validation.touched.lastname && validation.errors.lastname ? (
                  <FormFeedback type="invalid">
                    {validation.errors.lastname}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col sm={3}>
              <div className="mb-3">
                <Label className="d-block mb-3">Active </Label>
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    onChange={value =>
                      validation.setFieldValue("active", value.target.checked)
                    }
                    defaultChecked={validation.values?.active}
                    name="active"
                    className="form-check-input"
                    id="active1"
                  />
                  <Label className="form-check-label" htmlFor="active1">
                    Yes
                  </Label>
                </div>
                &nbsp;
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    onChange={value =>
                      validation.setFieldValue("active", !value.target.checked)
                    }
                    defaultChecked={validation.values?.active === false}
                    name="active"
                    className="form-check-input"
                    id="active2"
                  />
                  <Label className="form-check-label" htmlFor="active2">
                    No
                  </Label>
                </div>
              </div>
            </Col>
            <Col sm={3}>
              <div className="mb-3">
                <Label className="d-block mb-3">View Only</Label>
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    onChange={value =>
                      validation.setFieldValue("viewonly", value.target.checked)
                    }
                    defaultChecked={validation.values?.viewonly === true}
                    name="viewonly"
                    className="form-check-input"
                    id="viewonly1"
                  />
                  <Label className="form-check-label" htmlFor="viewonly1">
                    Yes
                  </Label>
                </div>
                &nbsp;
                <div className="form-check form-check-inline">
                  <Input
                    type="radio"
                    onChange={value =>
                      validation.setFieldValue(
                        "viewonly",
                        !value.target.checked
                      )
                    }
                    defaultChecked={validation.values?.viewonly === false}
                    name="viewonly"
                    className="form-check-input"
                    id="viewonly2"
                  />
                  <Label className="form-check-label" htmlFor="viewonly2">
                    No
                  </Label>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <div className="mb-3">
                {/* <Label className="form-label">Country</Label>
                <Input
                  id="country"
                  type="select"
                  name="country"
                  value={validation.values.country || ""}
                  onBlur={validation.handleBlur}
                  onChange={validation.handleChange}
                  invalid={
                    validation.touched.country && validation.errors.country
                      ? true
                      : false
                  }
                >
                  <option key={`optionVal_empty`} value="">
                    Please Select country
                  </option>
                  {(Countries || [])?.map((option, i) => (
                    <option key={`optionVal_${i}`} value={option?.id}>
                      {option?.name}
                    </option>
                  ))}
                </Input> */}

                {validation.touched.country && validation.errors.country ? (
                  <FormFeedback type="invalid">
                    {validation.errors.country}
                  </FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="text-end">
            <button type="submit" className="btn btn-primary save-user me-2">
              Save
            </button>
            <button
              type="button"
              color="primary"
              className="btn btn-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </Col>
      </Row>
      {isEdit && selectedRow?.id ? (
        <PrimaryDeviceModal
          show={showPrimaryModal}
          toggle={() => setShowPrimaryModal(false)}
          userId={selectedRow.id}
          onChanged={() => dispatch(getUserDevices(selectedRow.id))}
        />
      ) : null}
    </Form>
  )
}
export default UserSettingForm
